export interface Point {
  label: string;
  value: number;
}

/**
 * A deliberately small inline SVG line chart. No chart library for two data
 * series and a phone-sized screen.
 */
export function LineChart({
  points,
  unit = '',
  color = 'var(--accent)',
  height = 132,
}: {
  points: Point[];
  unit?: string;
  color?: string;
  height?: number;
}) {
  if (points.length < 2) {
    return (
      <div className="empty small">
        Not enough data yet — log at least two entries and the trend shows up here.
      </div>
    );
  }

  const w = 320;
  const h = height;
  const padX = 6;
  const padY = 16;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // A flat series would divide by zero; give it an artificial range.
  const span = max - min || Math.max(1, max * 0.1);
  const lo = min - span * 0.15;
  const hi = max + span * 0.15;

  const x = (i: number) => padX + (i / (points.length - 1)) * (w - padX * 2);
  const y = (v: number) => padY + (1 - (v - lo) / (hi - lo)) * (h - padY * 2);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${h - padY} L${padX},${h - padY} Z`;

  const last = points[points.length - 1]!;
  const first = points[0]!;
  const delta = last.value - first.value;

  return (
    <div>
      <svg className="chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img"
        aria-label={`Trend from ${first.value}${unit} to ${last.value}${unit}`}>
        <defs>
          <linearGradient id="chartfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#chartfill)" />
        <path d={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
          strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r="2.4" fill={color} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="row between tiny faint" style={{ marginTop: 4 }}>
        <span>{first.label}</span>
        <span className="mono" style={{ color: delta === 0 ? 'var(--muted)' : delta < 0 ? 'var(--ok)' : 'var(--warn)' }}>
          {delta > 0 ? '+' : ''}
          {Math.round(delta * 10) / 10}
          {unit}
        </span>
        <span>{last.label}</span>
      </div>
    </div>
  );
}

export function BarChart({
  points,
  unit = '',
  color = 'var(--accent)',
}: {
  points: Point[];
  unit?: string;
  color?: string;
}) {
  if (points.length === 0) {
    return <div className="empty small">Nothing logged yet.</div>;
  }
  const max = Math.max(...points.map((p) => p.value), 1);

  return (
    <div className="stack" style={{ gap: 5 }}>
      {points.map((p) => (
        <div className="recovery-row" key={p.label} style={{ gridTemplateColumns: '58px 1fr 64px' }}>
          <span className="name">{p.label}</span>
          <span className="bar" style={{ height: 10 }}>
            <span style={{ width: `${(p.value / max) * 100}%`, background: color }} />
          </span>
          <span className="pct mono">
            {Math.round(p.value)}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}
